import { useAudioPlayback } from '@livekit/components-react'
import { Volume1, Volume2, VolumeX } from 'lucide-react'
import { IconButton, Slider } from '@/components/play'
import { HOVER_QUERY, useMediaQuery } from '@/lib/watch/useMediaQuery'
import { useViewerVolume } from '@/lib/watch/useViewerVolume'
import { cn } from '@/lib/utils'
import './watch.css'

const PERCENT = { style: 'unit', unit: 'percent' } as const

export interface SoundControlProps {
  /**
   * The browser lets the page play sound (LiveKit's `canPlayAudio`). Once it has held sound back, nothing plays until a
   * first press anywhere on the page; the speaker says so ("Turn sound on"), and pressing it is that press.
   */
  allowed: boolean
  /**
   * A level slider beside the speaker, sliding out on hover or keyboard focus: for a pointer that hovers. A touch
   * screen's own buttons are its volume, so it gets the speaker alone, which mutes and unmutes.
   */
  slider: boolean
  className?: string
}

/**
 * The viewer's speaker on the video (docs/features/stage-volume.md): mutes and unmutes everything the page plays, and
 * with `slider`, sets its level. Both are this browser's own (`useViewerVolume`), remembered for every event. The glyph
 * follows what is heard: struck through while muted or held back by the browser, one wave below half, two above.
 */
export function SoundControl({ allowed, slider, className }: SoundControlProps) {
  const { level, muted, setLevel, setMuted } = useViewerVolume()
  const silent = !allowed || muted
  const Glyph = silent ? VolumeX : level < 50 ? Volume1 : Volume2
  const label = !allowed ? 'Turn sound on' : muted ? 'Unmute' : 'Mute'
  return (
    <div className={cn('watch-sound', className)} data-slider={slider || undefined}>
      {slider && (
        <Slider
          className="watch-sound__slider"
          label="Volume"
          hideLabel
          showValue={false}
          step={5}
          format={PERCENT}
          value={silent ? 0 : level}
          onValueChange={setLevel}
        />
      )}
      <IconButton variant="ghost" elevated size="compact" label={label} onClick={() => setMuted(allowed ? !muted : false)}>
        <Glyph />
      </IconButton>
    </div>
  )
}

/** The speaker as the watch screen shows it, inside its room: whether the browser lets it play, and a slider for a pointer that hovers. */
export function WatchSound() {
  const { canPlayAudio } = useAudioPlayback()
  const hovers = useMediaQuery(HOVER_QUERY)
  return <SoundControl allowed={canPlayAudio} slider={hovers} />
}
